import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useSearchParams } from "react-router";

import { useAuth } from "@/providers";

import {
  OAuthCallbackPage,
  type OAuthCallbackPageBaseProps,
} from "./OAuthCallbackPage";
import { resolveOAuthExchangeView } from "./resolveOAuthCallbackView";

export interface OAuthExchangePageProps extends OAuthCallbackPageBaseProps {
  /** This page renders a spinner while its own exchange runs, so the caption is required. */
  loadingMessage: React.ReactNode;
  /** Namespaces the exchange query key so two providers' codes can never collide. */
  provider: string;
  /**
   * Exchanges the single-use authorization code — the API does the exchange, so the
   * provider's token never touches the browser. Never called without an org: the query's
   * `enabled` gate withholds the exchange until one is resolved.
   */
  exchange: (params: {
    code: string;
    state: string;
    orgId: string;
  }) => Promise<unknown>;
}

/**
 * A callback page that runs its own exchange, for providers whose route is not in the data
 * router (a descendant `<Routes>` swallows loaders, so they cannot have one). Where a loader
 * is available, prefer it — it settles the outcome before anything renders.
 */
export function OAuthExchangePage({
  provider,
  exchange,
  ...pageProps
}: OAuthExchangePageProps) {
  const [searchParams] = useSearchParams();
  const { currentOrganization, initialAuthCheckComplete, isLoading } =
    useAuth();
  const orgId = currentOrganization?.org_id;
  // The very first render can see no org with nothing in flight (the boot check only starts
  // in an effect), so "no org" is only trustworthy once the initial check has completed and
  // no sign-in is pending.
  const authSettled = initialAuthCheckComplete && !isLoading;

  // Latched once: stripping the URL after the exchange settles must not flip this mount to
  // "missing params". `orgId` deliberately stays live so an org that resolves late enables
  // the exchange instead of stranding it.
  const [params] = React.useState(() => ({
    code: searchParams.get("code"),
    state: searchParams.get("state"),
    providerError: searchParams.get("error"),
    providerErrorDescription: searchParams.get("error_description"),
  }));
  const { code, state, providerError } = params;

  const exchangeQuery = useQuery({
    // Keyed on the single-use code and deliberately OUTSIDE every domain key factory — it
    // must never move under one, because invalidating this key re-sends a spent code. The org
    // is deliberately absent for the same reason: it is re-derived per entry, and a key that
    // moved with it would re-send the code the moment the current org changed.
    queryKey: ["oauthExchange", provider, code, state],
    queryFn: () => {
      if (!code || !state || !orgId) {
        // Unreachable behind `enabled`; refuse rather than assert non-null.
        return Promise.reject(
          new Error("OAuth exchange invoked without required context."),
        );
      }
      return exchange({ code, state, orgId });
    },
    enabled:
      authSettled &&
      providerError === null &&
      code !== null &&
      state !== null &&
      orgId !== undefined,
    // retryOnMount is the load-bearing option: v5 refetches an errored query on remount by
    // default, which would re-send the spent code and bury the real failure as
    // `invalid_code`. The rest pin the outcome as immutable for the life of the tab.
    retryOnMount: false,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const view = React.useMemo(
    () =>
      resolveOAuthExchangeView({
        ...params,
        orgId,
        authSettled,
        exchangeStatus: exchangeQuery.status,
        exchangeError: exchangeQuery.error,
      }),
    [params, orgId, authSettled, exchangeQuery.status, exchangeQuery.error],
  );

  return <OAuthCallbackPage view={view} {...pageProps} />;
}
